/**
 * A container's expenses as a table, with the total per currency under it -
 * CNY and USD are never added together. Each line can be deleted only where
 * onRemove is given: the Expenses window has it, the container's edit form
 * shows the same lines to look at only.
 */

import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import { money, byCurrency, currencyLine, docNo } from "layouts/accounting/api";

const cell = { fontSize: 13, whiteSpace: "nowrap" };

function ExpensesTable({ rows, onRemove }) {
  if (rows === null) {
    return <SuiTypography sx={{ fontSize: 14, opacity: 0.6 }}>Loading...</SuiTypography>;
  }
  if (rows.length === 0) {
    return <SuiTypography sx={{ fontSize: 14, opacity: 0.6 }}>No expenses yet</SuiTypography>;
  }

  return (
    <SuiBox sx={{ overflowX: "auto" }}>
      <Table sx={{ "& .MuiTableCell-root": { px: 1, py: 0.75 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>From</TableCell>
            <TableCell>Note</TableCell>
            <TableCell>Voucher</TableCell>
            {onRemove && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell sx={cell}>{r.date}</TableCell>
              <TableCell sx={{ ...cell, fontWeight: 700 }}>{r.category || "-"}</TableCell>
              <TableCell align="right" sx={{ ...cell, fontWeight: 700 }}>
                {`${money(r.amount)} ${r.currency}`}
              </TableCell>
              <TableCell sx={cell}>{r.box || "-"}</TableCell>
              <TableCell sx={{ ...cell, whiteSpace: "normal" }}>{r.description || "-"}</TableCell>
              <TableCell sx={{ ...cell, opacity: 0.6 }}>{docNo("payment", r.number)}</TableCell>
              {onRemove && (
                <TableCell sx={cell}>
                  <IconButton
                    size="small"
                    aria-label={`Delete expense ${r.id}`}
                    onClick={() => onRemove(r)}
                    sx={{ color: "#D32F2F" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <SuiTypography sx={{ fontSize: 14, fontWeight: 700, pt: 1.5 }}>
        Total · المجموع: {currencyLine(byCurrency(rows, (r) => r.amount))}
      </SuiTypography>
    </SuiBox>
  );
}

ExpensesTable.defaultProps = { rows: null, onRemove: null };

ExpensesTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  onRemove: PropTypes.func,
};

export default ExpensesTable;
